import { ensureRunning, getAudioContext } from './audioUnlock';

/**
 * Play a decoded buffer through Web Audio, abortable, with zero-progress detection.
 *
 * Abort has a hard budget: global STOP must silence the device within ~100 ms (non-negotiable
 * §3.3). `AudioBufferSourceNode.stop()` is effectively instant, so the budget is met by
 * construction — the risk is not latency but forgetting to wire the signal, which is why the
 * signal is a required argument rather than an option.
 *
 * Zero-progress detection exists because of the silent-mode unknown (see `audioSession.ts`). If
 * `currentTime` does not advance while we believe we are speaking, the person is tapping a
 * device that is making no sound, and they must be told rather than left to find out socially.
 */

export interface PlaybackResult {
  /** Playback ran to completion. */
  completed: boolean;
  /** Aborted by STOP. Not a failure. */
  aborted: boolean;
  /** The context clock did not advance — suspect silent mode or a dead output. */
  silent: boolean;
  durationMs: number;
  /**
   * `performance.now()` at the moment `source.start()` returned, or `undefined` if it never did.
   *
   * This is the only place in the Web Audio path that knows when sound *began*: this function
   * resolves on `ended`, so a caller timing it measures the end of the utterance instead. That
   * distinction is the whole of what `DIA-01`'s `msToAudio` needed and did not have (C80).
   */
  startedPlayingAtMs?: number;
}

export async function playBuffer(
  buffer: AudioBuffer,
  signal: AbortSignal,
  volume = 1,
): Promise<PlaybackResult> {
  if (signal.aborted) {
    return { completed: false, aborted: true, silent: false, durationMs: 0 };
  }

  const running = await ensureRunning();
  const ctx = getAudioContext();
  if (!ctx || !running) {
    throw new Error('audio_context_not_running');
  }

  const gain = ctx.createGain();
  gain.gain.value = volume;
  const source = ctx.createBufferSource();
  source.buffer = buffer;
  source.connect(gain).connect(ctx.destination);

  const startedClock = ctx.currentTime;
  const expectedMs = buffer.duration * 1000;

  return new Promise<PlaybackResult>((resolve) => {
    let settled = false;
    let startedPlayingAtMs: number | undefined;

    const finish = (result: PlaybackResult): void => {
      if (settled) return;
      settled = true;
      signal.removeEventListener('abort', onAbort);
      try {
        source.disconnect();
        gain.disconnect();
      } catch {
        // Already torn down by the graph; nothing to do.
      }
      resolve(result);
    };

    const onAbort = (): void => {
      try {
        source.stop();
      } catch {
        // stop() on a finished source throws; that is the same outcome we wanted.
      }
      finish({ completed: false, aborted: true, silent: false, durationMs: 0 });
    };

    source.addEventListener('ended', () => {
      const advanced = ctx.currentTime - startedClock;
      // A tolerance rather than an exact comparison: the clock is not sample-accurate, and a
      // short utterance ("Ja") legitimately advances it very little.
      const silent = expectedMs > 250 && advanced * 1000 < expectedMs * 0.5;
      finish({
        completed: true,
        aborted: false,
        silent,
        durationMs: Math.round(advanced * 1000),
        ...(startedPlayingAtMs === undefined ? {} : { startedPlayingAtMs }),
      });
    });

    signal.addEventListener('abort', onAbort, { once: true });

    try {
      source.start();
      startedPlayingAtMs = performance.now();
    } catch (err) {
      finish({ completed: false, aborted: false, silent: false, durationMs: 0 });
      console.error('[audio] source.start failed', err);
    }
  });
}

/**
 * Decode compressed audio. Works on a suspended context — only playback needs `running` — so a
 * fetch can be decoded while the app is waiting for the person's first tap.
 */
export async function decode(bytes: ArrayBuffer): Promise<AudioBuffer> {
  const ctx = getAudioContext();
  if (!ctx) throw new Error('audio_context_unavailable');
  return ctx.decodeAudioData(bytes);
}
